import { useState, useEffect } from 'react'
import {
	TextField,
	Button,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	Typography,
	Box,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	IconButton,
	Alert
} from '@mui/material'
import { AttachFile, Delete } from '@mui/icons-material'
import { useBatch } from '../hooks/useBatch'
import { CreateBatchPayload } from '../models/batch'
import { useCrop } from '../hooks/useCrop'
import { useCategory } from '../hooks/useCategory'
import useRole from '../hooks/useRole'

export const CreateBatch = () => {
	const { createBatch } = useBatch()
	const { crops, fetchCrops } = useCrop()
	const { categories, fetchCategories } = useCategory()
	const { canWrite } = useRole()

	// Deshabilitar formulario para usuarios sin permisos de escritura
	const isFormDisabled = !canWrite()

	const [lote, setLote] = useState('')
	const [fechaIngreso, setFechaIngreso] = useState('')

	const [semilla, setSemilla] = useState({
		cultivo: '',
		categoria: '',
		germoplasma: '',
		propietario: '',
		procedencia: '',
		numeroBultos: '',
		cantidadBruta: '',
		tara: '',
		cantidadNeta: '',
		cardNumber: '',
		humedad: '',
		plaga: '',
		notas: '',
		expiration: 6
	})

	const [attachments, setAttachments] = useState<
		Array<{ ext: string; data: string; name: string }>
	>([])

	useEffect(() => {
		fetchCrops()
		fetchCategories()
	}, [fetchCrops, fetchCategories])

	const handleSemillaChange = (field: string, value: string) => {
		setSemilla({ ...semilla, [field]: value })
	}

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files
		if (files) {
			const newAttachments: Array<{ ext: string; data: string; name: string }> = []
			for (let i = 0; i < files.length; i++) {
				const file = files[i]
				const reader = new FileReader()
				reader.onload = () => {
					const base64Data = reader.result?.toString().split(',')[1] || ''
					const ext = file.name.split('.').pop() || ''
					newAttachments.push({ ext, data: base64Data, name: file.name })
					if (newAttachments.length === files.length) {
						setAttachments([...attachments, ...newAttachments])
					}
				}
				reader.readAsDataURL(file)
			}
		}
	}

	const handleRemoveAttachment = (index: number) => {
		const updatedAttachments = [...attachments]
		updatedAttachments.splice(index, 1)
		setAttachments(updatedAttachments)
	}

	const handleSaveLote = async () => {
		const payload: CreateBatchPayload = {
			batchId: lote,
			date: fechaIngreso || '',
			crop: semilla.cultivo,
			germplasm: semilla.germoplasma,
			category: semilla.categoria,
			owner: semilla.propietario,
			origin: semilla.procedencia,
			bulk: Number(semilla.numeroBultos) || 0,
			grossweight: Number(semilla.cantidadBruta) || 0,
			tare: Number(semilla.tara) || 0,
			netweight: Number(semilla.cantidadNeta) || 0,
			cardNumber: semilla.cardNumber || undefined,
			humidity: Number(semilla.humedad) || 0,
			plague: semilla.plaga,
			notes: semilla.notas || '',
			active: true,
			rejection: 0,
			available: 0,
			losses: 0,
			expiration: Number(semilla.expiration) || 6,
			attachments: attachments.map(({ ext, data, name }) => ({ ext, data, name }))
		}

		const newBatch = await createBatch(payload)
		if (newBatch) {
			alert('Lote creado con éxito!')
			// Limpia el formulario o redirige
		}
	}

	return (
		<Box p={3}>
			{!canWrite() && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					No tienes permisos para crear lotes. Solo puedes ver información existente.
				</Alert>
			)}
			
			<Box display="flex" alignItems="center" mb={2}>
				<Typography variant="h4" gutterBottom>
					Ingreso de Lote
				</Typography>
				<Button 
					variant="contained" 
					sx={{ marginLeft: 4 }} 
					onClick={handleSaveLote}
					disabled={!canWrite()}
				>
					Guardar Lote
				</Button>
			</Box>

			<Box display="flex" alignItems="center" mb={2}>
				<TextField
					label="# de Lote"
					variant="outlined"
					sx={{ flex: 2, marginRight: 2 }}
					value={lote}
					onChange={(e) => setLote(e.target.value)}
					disabled={isFormDisabled}
				/>
				<TextField
					label="Fecha de Ingreso"
					type="date"
					InputLabelProps={{ shrink: true }}
					sx={{ flex: 2, marginRight: 2 }}
					value={fechaIngreso}
					onChange={(e) => setFechaIngreso(e.target.value)}
					disabled={isFormDisabled}
				/>
				<Button
					variant="outlined"
					startIcon={<AttachFile />}
					sx={{ flex: 1, marginRight: 2 }}
					component="label"
					disabled={isFormDisabled}
				>
					Archivos
					<input type="file" multiple hidden onChange={handleFileUpload} />
				</Button>
			</Box>

			<Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
				Tamaño máximo por archivo: 100MB
			</Typography>

			<Box>
				<Typography variant="h6" gutterBottom>
					Archivos Subidos
				</Typography>
				<List>
					{attachments.map((attachment, index) => (
						<ListItem
							key={index}
							secondaryAction={
								!isFormDisabled && (
									<IconButton edge="end" onClick={() => handleRemoveAttachment(index)}>
										<Delete />
									</IconButton>
								)
							}
						>
							<ListItemIcon>
								<AttachFile />
							</ListItemIcon>
							<ListItemText primary={attachment.name} secondary={`Extensión: ${attachment.ext}`} />
						</ListItem>
					))}
				</List>
			</Box>

			<Box>
				<FormControl fullWidth sx={{ marginBottom: 2 }}>
					<InputLabel>Cultivo</InputLabel>
					<Select
						value={semilla.cultivo}
						onChange={(e) => handleSemillaChange('cultivo', e.target.value)}
						disabled={isFormDisabled}
					>
						{crops.map((crop) => (
							<MenuItem key={crop.uid} value={crop.uid}>
								{crop.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					sx={{ marginBottom: 2 }}
				>
					<FormControl fullWidth sx={{ marginRight: 1 }}>
						<InputLabel>Categoría</InputLabel>
						<Select
							value={semilla.categoria}
							onChange={(e) => handleSemillaChange('categoria', e.target.value)}
							disabled={isFormDisabled}
						>
							{categories.map((category) => (
								<MenuItem key={category.uid} value={category.uid}>
									{category.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl fullWidth sx={{ marginLeft: 1 }}>
						<InputLabel>Germoplasma</InputLabel>
						<Select
							value={semilla.germoplasma}
							onChange={(e) => handleSemillaChange('germoplasma', e.target.value)}
							disabled={isFormDisabled}
						>
							{crops
								.filter((crop) => crop.uid === semilla.cultivo)
								.flatMap((crop) => crop.germplasm)
								.map((germplasm) => (
									<MenuItem key={germplasm._id} value={germplasm.name}>
										{germplasm.name}
									</MenuItem>
								))}
						</Select>
					</FormControl>
				</Box>

				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					sx={{ marginBottom: 2 }}
				>
					<TextField
						label="Propietario"
						fullWidth
						sx={{ marginRight: 1 }}
						value={semilla.propietario}
						onChange={(e) => handleSemillaChange('propietario', e.target.value)}
						disabled={isFormDisabled}
					/>
					<TextField
						label="Procedencia"
						fullWidth
						sx={{ marginLeft: 1 }}
						value={semilla.procedencia}
						onChange={(e) => handleSemillaChange('procedencia', e.target.value)}
						disabled={isFormDisabled}
					/>
				</Box>

				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					sx={{ marginBottom: 2 }}
				>
					<TextField
						label="Número de Bultos"
						type="number"
						fullWidth
						sx={{ marginRight: 1 }}
						value={semilla.numeroBultos}
						onChange={(e) => handleSemillaChange('numeroBultos', e.target.value)}
						disabled={isFormDisabled}
					/>
					<TextField
						label="Cantidad Bruta (Quintales)"
						type="number"
						fullWidth
						sx={{ marginLeft: 1 }}
						value={semilla.cantidadBruta}
						onChange={(e) => handleSemillaChange('cantidadBruta', e.target.value)}
						disabled={isFormDisabled}
					/>
				</Box>

				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					sx={{ marginBottom: 2 }}
				>
					<TextField
						label="Tara (Quintales)"
						type="number"
						fullWidth
						sx={{ marginRight: 1 }}
						value={semilla.tara}
						onChange={(e) => handleSemillaChange('tara', e.target.value)}
						disabled={isFormDisabled}
					/>
					<TextField
						label="Cantidad Neta (Quintales)"
						type="number"
						fullWidth
						sx={{ marginLeft: 1 }}
						value={semilla.cantidadNeta}
						onChange={(e) => handleSemillaChange('cantidadNeta', e.target.value)}
						disabled={isFormDisabled}
					/>
				</Box>

				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					sx={{ marginBottom: 2 }}
				>
					<TextField
						label="No. Tarjeta"
						fullWidth
						sx={{ marginRight: 1 }}
						value={semilla.cardNumber}
						onChange={(e) => handleSemillaChange('cardNumber', e.target.value)}
						disabled={isFormDisabled}
					/>
					<TextField
						label="Meses de Caducidad"
						type="number"
						InputProps={{ inputProps: { min: 1 } }}
						defaultValue={6}
						fullWidth
						sx={{ marginLeft: 1}}
						onChange={(e) => handleSemillaChange('expiration', e.target.value)}
						disabled={isFormDisabled}
					/>
				</Box>

				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					sx={{ marginBottom: 2 }}
				>
					<TextField
						label="Humedad (%)"
						type="number"
						fullWidth
						sx={{ marginRight: 1 }}
						value={semilla.humedad}
						onChange={(e) => handleSemillaChange('humedad', e.target.value)}
						disabled={isFormDisabled}
					/>
					<TextField
						label="Plaga"
						fullWidth
						sx={{ marginLeft: 1 }}
						value={semilla.plaga}
						onChange={(e) => handleSemillaChange('plaga', e.target.value)}
						disabled={isFormDisabled}
					/>
				</Box>

				<TextField
					label="Notas Generales"
					multiline
					rows={4}
					fullWidth
					sx={{ marginBottom: 2 }}
					value={semilla.notas}
					onChange={(e) => handleSemillaChange('notas', e.target.value)}
					disabled={isFormDisabled}
				/>

			</Box>
		</Box>
	)
}
